import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, Loader2, Sparkles, Camera, X } from "lucide-react";
import { BRANDS } from "../lib/brands";

// (Brand library moved to /lib/brands.js — shared across forms.)

const INITIAL = {
  brand: "Hot Wheels", release_name: "", vehicle: "", scale: "1:64",
  series: "", variant_type: "Standard", colorway: "",
  estimated_market_value: "", purchase_price: "", quantity: 1,
  condition: "Mint", packaging_condition: "Mint",
  msrp: "", rarity: "Common", is_chase: false, is_blkltd: false,
  notes: "",
};

function RecentChips({ label, values, current, onPick, testIdPrefix }) {
  if (!values || values.length === 0) return null;
  return (
    <div data-testid={`${testIdPrefix}-row`} className="flex items-center gap-1.5 mt-1 mb-2 flex-wrap">
      <span className="text-[10px] text-zinc-600 uppercase tracking-wider mr-1">{label}:</span>
      {values.map((v) => (
        <button
          key={v}
          type="button"
          data-testid={`${testIdPrefix}-${v.replace(/\s+/g, '-').toLowerCase()}`}
          onClick={() => onPick(v)}
          className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
            current === v
              ? "bg-rose-600/20 text-rose-300 border-rose-500/40"
              : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-rose-500/30 hover:text-rose-400"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

export default function AddCustomItemDialog({
  onCreated,
  // Controlled mode — used by parents that want to drive the dialog
  // externally (e.g. QuickScanDialog → "Add as Custom Item" handoff).
  // When `open`/`onOpenChange` are both provided, the trigger button is
  // hidden and the parent owns the open state.
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  // Optional prefill payload — applied each time the dialog opens.
  // Matches the shape of `INITIAL`. Useful for handing AI-identified
  // diecast data straight into the form so the user doesn't re-type.
  prefillData = null,
  // Optional photo (data URL). Pre-populated as the photo preview so the
  // user keeps their original scan instead of re-uploading.
  prefillPhoto = null,
}) {
  const isControlled = controlledOpen !== undefined && typeof controlledOnOpenChange === "function";
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? controlledOnOpenChange : setInternalOpen;
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [recents, setRecents] = useState({ brands: [], vehicles: [], series: [] });
  const [duplicate, setDuplicate] = useState(null);  // {match, total_quantity, total_spent, entry_count}
  const [incrementing, setIncrementing] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // The form is "dirty" when the user has typed/picked anything beyond the
  // INITIAL defaults OR uploaded a photo preview. We intentionally skip
  // dirtiness detection for purely AI-driven changes (identify/estimate)
  // because the user explicitly invoked those — closing then is still a
  // discard from their perspective.
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(INITIAL) || !!previewUrl,
    [form, previewUrl]
  );

  const resetAll = () => {
    setForm(INITIAL);
    setPreviewUrl(null);
    setConfidence(null);
    setDuplicate(null);
  };

  const handleOpenChange = async (next) => {
    if (!next) {
      if (saving || identifying || estimating || incrementing) return; // never close mid-action
      if (isDirty) {
        setConfirmCloseOpen(true);
        return;
      }
      setOpen(false);
      resetAll();
      return;
    }
    setOpen(true);
    try {
      const { data } = await api.get("/custom-items/recents?limit=5");
      setRecents({
        brands: data.brands || [],
        vehicles: data.vehicles || [],
        series: data.series || [],
      });
    } catch {
      setRecents({ brands: [], vehicles: [], series: [] });
    }
  };

  const confirmDiscard = () => {
    setConfirmCloseOpen(false);
    setOpen(false);
    resetAll();
  };

  // Global shortcut: Cmd/Ctrl+K opens the dialog from anywhere on the page
  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (isCmdK) {
        e.preventDefault();
        handleOpenChange(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply prefill payload (and photo) the moment the dialog opens. We hydrate
  // here rather than in handleOpenChange so a parent that flips `open` on its
  // own (controlled mode) still gets the data populated.
  useEffect(() => {
    if (!open) return;
    if (prefillData) {
      setForm((prev) => ({ ...prev, ...prefillData }));
    }
    if (prefillPhoto) {
      setPreviewUrl(prefillPhoto);
      setConfidence("medium");  // Came from AI scan — surface "AI medium" badge.
    }
    // Run only when the dialog opens; avoid re-running on every form keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced duplicate check whenever brand + name are both filled
  useEffect(() => {
    if (!open) return;
    const brand = (form.brand || "").trim();
    const name = (form.release_name || "").trim();
    if (!brand || !name || name.length < 3) {
      setDuplicate(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/custom-items/check-duplicate", {
          params: { brand, release_name: name },
        });
        setDuplicate(data?.match ? data : null);
      } catch {
        setDuplicate(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.brand, form.release_name]);

  const handleIncrement = async () => {
    if (!duplicate?.match) return;
    setIncrementing(true);
    try {
      await api.post(`/custom-items/${duplicate.match.release_id}/increment`);
      toast.success(`Added +1 to existing ${duplicate.match.brand} · ${duplicate.match.release_name}`);
      resetAll();
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setIncrementing(false);
    }
  };

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Resize a File to max `maxDim` on the longest side, re-encode as JPEG.
  // Keeps uploads fast and under the 6MB cap even for 48MP phone cameras.
  const resizeImage = (file, maxDim = 1280, quality = 0.82) => new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleIdentify = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-selecting same file
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use a JPEG, PNG, or WEBP image");
      return;
    }
    setIdentifying(true);
    try {
      const dataUrl = await resizeImage(file);
      setPreviewUrl(dataUrl);
      setConfidence(null);
      toast.info("Analyzing photo with AI...");
      const { data } = await api.post("/ai/identify-diecast", { image_base64: dataUrl });
      const filled = [];
      if (data.brand) { update("brand", data.brand); filled.push("brand"); }
      if (data.release_name) { update("release_name", data.release_name); filled.push("name"); }
      if (data.vehicle) { update("vehicle", data.vehicle); filled.push("vehicle"); }
      if (data.variant_type) update("variant_type", data.variant_type);
      if (data.colorway) { update("colorway", data.colorway); filled.push("colorway"); }
      // Tier flags inferred from packaging cues (chase wheels, Super-TH double-flame, RLC dot, BLKLTD sleeve, etc.)
      if (data.is_chase) { update("is_chase", true); filled.push("Chase"); }
      if (data.is_blkltd) { update("is_blkltd", true); filled.push("Super-TH/BLKLTD"); }
      if (data.rarity && data.rarity !== "Common") { update("rarity", data.rarity); filled.push(`rarity=${data.rarity}`); }
      if (data.msrp) { update("msrp", String(data.msrp)); filled.push(`MSRP $${data.msrp}`); }
      setConfidence(data.confidence || "low");
      if (filled.length) {
        toast.success(`Identified (${data.confidence}) — filled ${filled.join(", ")}. Review before saving.`);
      } else {
        toast.warning("AI couldn't identify this one. Fill the fields manually — photo is still saved.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identification failed");
      setConfidence(null);
    } finally {
      setIdentifying(false);
    }
  };

  const handleEstimate = async () => {
    if (!form.brand || !form.release_name) {
      toast.error("Enter brand and name first");
      return;
    }
    setEstimating(true);
    try {
      const { data } = await api.post("/ai/estimate-custom", {
        brand: form.brand,
        name: form.release_name,
        vehicle: form.vehicle,
        variant: form.variant_type,
      });
      if (data?.estimated_price) {
        update("estimated_market_value", data.estimated_price.toString());
        toast.success(`AI estimate: $${data.estimated_price}`);
      } else {
        toast.error("Couldn't estimate — please enter manually");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI estimate failed");
    } finally {
      setEstimating(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!form.brand || !form.release_name) {
      toast.error("Brand and name are required");
      return;
    }
    // If a duplicate is shown, require explicit "Create new anyway" via increment being dismissed
    if (duplicate?.match && !duplicate.dismissed) {
      toast.warning("You already have this — use the banner to add +1 or dismiss it");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_market_value: form.estimated_market_value ? parseFloat(form.estimated_market_value) : null,
        msrp: form.msrp ? parseFloat(form.msrp) : null,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        quantity: parseInt(form.quantity, 10) || 1,
        is_chase: !!form.is_chase,
        is_blkltd: !!form.is_blkltd,
        rarity: form.rarity || "Common",
        photo_base64: previewUrl || null,
      };
      await api.post("/custom-items", payload);
      toast.success(`Added ${form.brand} · ${form.release_name}`);
      resetAll();
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button
            data-testid="add-custom-item-btn"
            variant="outline"
            size="sm"
            className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-rose-500/40"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />Add Custom Item
          </Button>
        </DialogTrigger>
      )}
      <DialogContent data-testid="add-custom-item-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-800/50">
          <DialogTitle className="font-heading text-xl text-zinc-100 flex items-center gap-2">
            Add a custom diecast
            {isDirty && (
              <span
                data-testid="custom-item-dirty-pill"
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/40 font-semibold"
                title="You have unsaved changes"
              >
                unsaved
              </span>
            )}
          </DialogTitle>
          <p className="text-xs text-zinc-500">For Hot Wheels, Tarmac Works, Tomica, and anything else not in the Kaido House catalog.</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Quick Add by Photo */}
        <div data-testid="quick-add-panel" className="p-3 rounded-xl bg-gradient-to-r from-rose-950/30 to-amber-950/20 border border-rose-500/20 flex items-center gap-3">
          {previewUrl ? (
            <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-rose-500/30 flex-shrink-0">
              <img src={previewUrl} alt="preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => { setPreviewUrl(null); setConfidence(null); }}
                data-testid="clear-preview-btn"
                className="absolute -top-1 -right-1 bg-zinc-950 border border-zinc-700 rounded-full p-0.5 text-zinc-400 hover:text-rose-400"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="w-16 h-16 rounded-lg bg-zinc-900/60 border border-zinc-800 flex items-center justify-center flex-shrink-0">
              <Camera className="w-6 h-6 text-rose-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-zinc-100">Quick Add by Photo</p>
              {confidence && (
                <span
                  data-testid="confidence-badge"
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                    confidence === "high"
                      ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                      : confidence === "medium"
                        ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                        : "bg-zinc-700/30 text-zinc-400 border border-zinc-600/30"
                  }`}
                >
                  <Sparkles className="w-2.5 h-2.5" />AI {confidence}
                </span>
              )}
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
              {previewUrl ? "Photo will be saved to this entry's gallery." : "AI reads the packaging and auto-fills the form."}
            </p>
          </div>
          <label
            data-testid="quick-add-photo-btn"
            className={`cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
              identifying
                ? "bg-zinc-800 text-zinc-500"
                : "bg-rose-600 hover:bg-rose-500 text-white"
            }`}
          >
            {identifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            {identifying ? "Analyzing..." : (previewUrl ? "Replace" : "Pick Photo")}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={handleIdentify}
              disabled={identifying}
              data-testid="quick-add-photo-input"
            />
          </label>
        </div>

        {duplicate?.match && !duplicate.dismissed && (
          <div data-testid="duplicate-banner" className="mt-3 p-3 rounded-xl bg-amber-950/20 border border-amber-500/25 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-0.5">You already have this</p>
              <p className="text-sm text-zinc-100 truncate">
                <span className="font-medium">{duplicate.match.brand}</span> &middot; {duplicate.match.release_name}
              </p>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Qty {duplicate.total_quantity} &middot; Spent ${duplicate.total_spent.toFixed(2)} &middot; {duplicate.entry_count} purchase{duplicate.entry_count !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <Button
                type="button"
                data-testid="dup-increment-btn"
                onClick={handleIncrement}
                disabled={incrementing}
                size="sm"
                className="bg-amber-600 hover:bg-amber-500 text-black font-semibold"
              >
                {incrementing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Add +1 to existing
              </Button>
              <button
                type="button"
                data-testid="dup-dismiss-btn"
                onClick={() => setDuplicate((d) => d ? { ...d, dismissed: true } : null)}
                className="text-[10px] text-zinc-500 hover:text-zinc-300 underline underline-offset-2"
              >
                Create separate entry anyway
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Brand *</Label>
            <RecentChips label="Recent" values={recents.brands} current={form.brand} onPick={(v) => update("brand", v)} testIdPrefix="recent-brand" />
            <Input
              data-testid="custom-brand-select"
              list="custom-item-brands"
              value={form.brand}
              onChange={(e) => update("brand", e.target.value)}
              className="bg-zinc-900 border-zinc-800 mt-1"
              placeholder="Start typing to search…"
              autoComplete="off"
            />
            <datalist id="custom-item-brands">
              {BRANDS.map((b) => <option key={b} value={b} />)}
            </datalist>
            <p className="text-[10px] text-zinc-600 mt-1">{BRANDS.length - 1}+ brands searchable &middot; or type your own</p>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Scale</Label>
            <Input data-testid="custom-scale-input" value={form.scale} onChange={(e) => update("scale", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="1:64" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Release Name *</Label>
            <Input data-testid="custom-name-input" value={form.release_name} onChange={(e) => update("release_name", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Nissan Skyline GT-R R34 V-spec II" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Vehicle</Label>
            <RecentChips label="Recent" values={recents.vehicles} current={form.vehicle} onPick={(v) => update("vehicle", v)} testIdPrefix="recent-vehicle" />
            <Input data-testid="custom-vehicle-input" value={form.vehicle} onChange={(e) => update("vehicle", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Nissan Skyline R34" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Series</Label>
            <RecentChips label="Recent" values={recents.series} current={form.series} onPick={(v) => update("series", v)} testIdPrefix="recent-series" />
            <Input data-testid="custom-series-input" value={form.series} onChange={(e) => update("series", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Japan Historics" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Variant</Label>
            <Input data-testid="custom-variant-input" value={form.variant_type} onChange={(e) => update("variant_type", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Standard" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Colorway</Label>
            <Input data-testid="custom-colorway-input" value={form.colorway} onChange={(e) => update("colorway", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Midnight Purple" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tier &amp; rarity (helps AI value)</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <button
                type="button"
                data-testid="custom-chase-toggle"
                onClick={() => update("is_chase", !form.is_chase)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  form.is_chase
                    ? "bg-amber-500/20 border-amber-400/60 text-amber-200 shadow-[0_0_14px_rgba(245,158,11,0.25)]"
                    : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-amber-500/40 hover:text-amber-300"
                }`}
              >
                ★ Chase
              </button>
              <button
                type="button"
                data-testid="custom-blkltd-toggle"
                onClick={() => update("is_blkltd", !form.is_blkltd)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  form.is_blkltd
                    ? "bg-rose-500/20 border-rose-400/60 text-rose-200 shadow-[0_0_14px_rgba(244,63,94,0.25)]"
                    : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-rose-500/40 hover:text-rose-300"
                }`}
                title="BLKLTD / Super Treasure Hunt / RLC / Limited tier"
              >
                ◆ Super-TH / BLKLTD / RLC
              </button>
              <select
                data-testid="custom-rarity-select"
                value={form.rarity}
                onChange={(e) => update("rarity", e.target.value)}
                className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:border-zinc-500"
              >
                <option value="Common">Common</option>
                <option value="Uncommon">Uncommon</option>
                <option value="Rare">Rare</option>
                <option value="Very Rare">Very Rare</option>
                <option value="Grail">Grail</option>
              </select>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1.5">
              Mark these to help the AI estimate accurately. A Hot Wheels Super-TH gets ~$25-$80 vs. $2-$5 mainline.
            </p>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">MSRP (USD)</Label>
            <Input data-testid="custom-msrp-input" type="number" step="0.01" value={form.msrp} onChange={(e) => update("msrp", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="1.25" />
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Market Value (USD)</Label>
              <button
                data-testid="custom-ai-estimate-btn"
                type="button"
                onClick={handleEstimate}
                disabled={estimating}
                className="text-[11px] text-rose-400 hover:text-rose-300 flex items-center gap-1 disabled:opacity-50"
              >
                {estimating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Estimate with AI
              </button>
            </div>
            <Input data-testid="custom-value-input" type="number" step="0.01" value={form.estimated_market_value} onChange={(e) => update("estimated_market_value", e.target.value)} className="bg-zinc-900 border-zinc-800" placeholder="35.00" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Purchase Price (USD)</Label>
            <Input data-testid="custom-purchase-price-input" type="number" step="0.01" value={form.purchase_price} onChange={(e) => update("purchase_price", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="5.00" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Quantity</Label>
            <Input data-testid="custom-quantity-input" type="number" min="1" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Condition</Label>
            <Input data-testid="custom-condition-input" value={form.condition} onChange={(e) => update("condition", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Packaging</Label>
            <Input data-testid="custom-packaging-input" value={form.packaging_condition} onChange={(e) => update("packaging_condition", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
            <Textarea data-testid="custom-notes-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" rows={2} placeholder="Where you bought it, condition details, anything you want to remember" />
          </div>
        </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-zinc-800/50 bg-zinc-950">
          <p className="hidden sm:block text-[10px] text-zinc-600">
            <kbd className="px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 font-mono">⌘K</kbd> to open &middot;
            <kbd className="ml-2 px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 font-mono">Enter</kbd> to save &middot;
            <kbd className="ml-2 px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 font-mono">Esc</kbd> to close
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" data-testid="custom-cancel-btn" variant="outline" onClick={() => handleOpenChange(false)} className="bg-zinc-900 border-zinc-800 text-zinc-400">Cancel</Button>
            <Button type="submit" data-testid="custom-submit-btn" disabled={saving} className="bg-rose-600 hover:bg-rose-500 text-white">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Add to Collection
            </Button>
          </div>
        </div>
        </form>
      </DialogContent>
    </Dialog>

    <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
      <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="custom-item-discard-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">
            You've started filling this in but haven't saved yet. Closing now will lose your work — including any photo you uploaded.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="custom-item-discard-cancel"
            className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700 hover:text-white"
          >
            Keep editing
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="custom-item-discard-confirm-btn"
            onClick={confirmDiscard}
            className="bg-rose-600 hover:bg-rose-500 text-white"
          >
            Discard changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
